import { useCallback, useEffect, useState } from 'react';

/**
 * SeedFilterPreset is a saved filter combination for the seed catalog.
 * Stored in localStorage under the `seedFilterPresets:v1` key. User-local
 * only — no backend persistence (SPEC-014 §2).
 */
export interface SeedFilterPreset {
  id: string;
  name: string;
  params: Record<string, string>;
  createdAt: string;
}

const STORAGE_KEY = 'seedFilterPresets:v1';

function loadPresets(): SeedFilterPreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return parsed as SeedFilterPreset[];
    return [];
  } catch {
    return [];
  }
}

function savePresets(presets: SeedFilterPreset[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
  } catch {
    // ignore quota errors — presets are a quality-of-life feature
  }
}

function generateId(): string {
  // Small random id; localStorage-only, no collision risk in practice.
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export function useFilterPresets() {
  const [presets, setPresets] = useState<SeedFilterPreset[]>([]);

  // Load presets from localStorage on mount (external state source).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPresets(loadPresets());
  }, []);

  const savePreset = useCallback((name: string, params: URLSearchParams) => {
    const record: Record<string, string> = {};
    for (const [k, v] of params.entries()) {
      record[k] = v;
    }
    const preset: SeedFilterPreset = {
      id: generateId(),
      name,
      params: record,
      createdAt: new Date().toISOString(),
    };
    setPresets((prev) => {
      const next = [...prev, preset];
      savePresets(next);
      return next;
    });
  }, []);

  const deletePreset = useCallback((id: string) => {
    setPresets((prev) => {
      const next = prev.filter((p) => p.id !== id);
      savePresets(next);
      return next;
    });
  }, []);

  const applyPreset = useCallback(
    (id: string): URLSearchParams | null => {
      const preset = presets.find((p) => p.id === id);
      if (!preset) return null;
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(preset.params)) {
        params.set(k, v);
      }
      return params;
    },
    [presets],
  );

  return { presets, savePreset, deletePreset, applyPreset };
}
